/**
 * Video pre-flight validation before upload.
 *
 * The client reads <video> metadata to catch obvious rejects (bad
 * container, wrong dimensions, wrong duration) so the user sees a fast
 * error instead of a rejected upload. The server always re-validates
 * with ffprobe inside the transcode worker.
 */

const MAX_BYTES = 100 * 1024 * 1024;
const MAX_DURATION_S = 10.0;
const MAX_LONG_EDGE = 3840;
const ALLOWED = new Set(["video/mp4", "video/quicktime", "video/webm"]);

export async function inspectVideo(file) {
  if (!ALLOWED.has(file.type)) {
    throw new Error(
      `Unsupported video type ${file.type || "unknown"}. Use MP4, MOV, or WEBM.`,
    );
  }
  if (file.size > MAX_BYTES) {
    throw new Error(
      `Video is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 100 MB.`,
    );
  }

  const url = URL.createObjectURL(file);
  try {
    const meta = await new Promise((resolve, reject) => {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;
      el.onloadedmetadata = () => {
        resolve({
          duration_s: el.duration,
          width: el.videoWidth,
          height: el.videoHeight,
        });
      };
      el.onerror = () =>
        reject(new Error("Couldn't read this video — is the file corrupted?"));
      el.src = url;
    });

    if (!isFinite(meta.duration_s) || meta.duration_s === 0) {
      throw new Error("Couldn't determine the video length.");
    }
    if (meta.duration_s > MAX_DURATION_S + 0.25) {
      throw new Error(
        `Video is ${meta.duration_s.toFixed(1)}s — the limit is ${MAX_DURATION_S}s.`,
      );
    }
    if (Math.max(meta.width, meta.height) > MAX_LONG_EDGE) {
      throw new Error(
        `Video is ${meta.width}×${meta.height} — the limit is 3840 on the long edge.`,
      );
    }
    return {
      duration_ms: Math.round(meta.duration_s * 1000),
      width: meta.width,
      height: meta.height,
      content_type: file.type,
      size: file.size,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
